/**
 * Justifier une ligne de l'exercice en y déposant sa facture (équipe) : le
 * fichier entre dans la collecte comme « Déposer un fichier », la personne
 * vérifie les champs lus et la rubrique, puis la pièce est déposée dans le
 * Drive et jointe dans Zoho à cette dépense. La ligne est choisie par la
 * personne : aucun rapprochement n'est cherché.
 */

import { dateFr, eurosCentimes } from '@scc/core';
import { h, vider } from '../../dom.js';
import {
  corrigerPiece,
  deposerFichier,
  deposerPiece,
  detailPiece,
  ErreurPiece,
  joindreDansZoho,
  TAILLE_DEPOT_MAX,
  type CorrectionPiece,
  type DepotDrive,
  type LigneJustificatif,
  type Piece,
} from './api.js';
import { brut, creerRetour, messageDe, verrouille } from './communs.js';
import { ETATS_PIECE, messageDepotRefuse, messageEnvoiRefuse, refusAvantEnvoi } from './pieces-textes.js';

export const TYPES_FACTURE = '.pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic';

/** Une ligne reçoit une facture déposée : dépense Zoho sans pièce. */
export const ligneJustifiable = (l: LigneJustificatif): boolean => l.etat === 'a_justifier' && l.typeOperation === 'expense' && l.lienZoho !== null;

const montantSaisi = (v: string): number | null | 'invalide' => {
  const t = v.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : 'invalide';
};

/**
 * Panneau ouvert sous la ligne pour un fichier déposé. `fini` est appelé une
 * fois la pièce jointe dans Zoho, avec le message à montrer (la liste se relit alors).
 */
export function panneauJustifier(ligne: LigneJustificatif, fichier: File, fini: (message: string) => void): HTMLElement {
  const el = h('div', { classe: 'panneau-justifier' });
  const retour = creerRetour();
  const corps = h('div', {});
  el.append(h('p', {}, h('strong', {}, brut(fichier.name)), brut(` · pour la dépense du ${dateFr(ligne.date)} (${eurosCentimes(ligne.montant)})`)), corps, retour.el);

  const refus = refusAvantEnvoi(fichier, TAILLE_DEPOT_MAX);
  if (refus !== null) {
    retour.ko(refus);
    return el;
  }
  corps.appendChild(h('p', { classe: 'note-lecture' }, 'Lecture du fichier…'));

  void (async () => {
    let piece: Piece;
    try {
      const r = await deposerFichier(fichier);
      piece = r.piece;
      if (piece.etat === 'a_arbitrer') piece = (await corrigerPiece(piece.id, { nature: 'facture_scc' })).piece;
    } catch (e) {
      vider(corps);
      retour.ko(e instanceof ErreurPiece ? messageEnvoiRefuse(e.statut, e.message) : 'Le fichier n’a pas pu être envoyé.');
      return;
    }
    if (piece.etat !== 'a_valider' && piece.etat !== 'deposee' && piece.etat !== 'deja_classee') {
      vider(corps);
      retour.ko(`Ce fichier est déjà connu : pièce n° ${String(piece.id)}, ${ETATS_PIECE[piece.etat].toLowerCase()}. Reprenez-la d’abord dans l’historique de l’onglet « Pièces reçues ».`);
      return;
    }
    let rubriques: { numero: string; libelle: string }[] = [];
    try {
      rubriques = (await detailPiece(piece.id)).rubriquesPossibles;
    } catch {
      /* Sans la liste du Drive, la rubrique proposée reste seule au choix. */
    }
    formulaire(piece, rubriques);
  })();

  function formulaire(piece: Piece, rubriques: readonly { numero: string; libelle: string }[]): void {
    vider(corps);
    const c = piece.champs;
    const aDeposer = piece.etat === 'a_valider';
    // Ce que le fichier n'a pas livré se reprend de la ligne : la personne l'a choisie pour cette facture.
    const fournisseur = h('input', { type: 'text', maxlength: 200, value: c?.fournisseur.valeur ?? (ligne.tiers || '') });
    const date = h('input', { type: 'date', value: c?.date.valeur ?? ligne.date });
    const montantLu = c?.montant.valeur ?? (c?.devise.valeur && c.devise.valeur !== 'EUR' ? null : ligne.montant);
    const montant = h('input', { type: 'text', inputmode: 'decimal', value: montantLu === null ? '' : montantLu.toFixed(2).replace('.', ',') });
    const devise = h('input', { type: 'text', maxlength: 3, value: c?.devise.valeur ?? 'EUR' });
    const rubrique = h('select', {}, h('option', { value: '' }, 'Rubrique du Drive : à choisir'));
    for (const r of rubriques) rubrique.appendChild(h('option', { value: r.numero }, brut(`${r.numero}. ${r.libelle}`)));
    if (piece.rubrique !== null && !rubriques.some((r) => r.numero === piece.rubrique)) rubrique.appendChild(h('option', { value: piece.rubrique }, `Rubrique ${piece.rubrique}`));
    rubrique.value = piece.rubrique ?? '';
    const bouton = h(
      'button',
      { type: 'button', classe: 'btn-action principal' },
      aDeposer ? 'Déposer dans le Drive et joindre à cette dépense' : 'Joindre à cette dépense dans Zoho',
    );
    const controles = [fournisseur, date, montant, devise, rubrique, bouton];
    corps.append(
      h('p', { classe: 'note-lecture' }, brut(piece.etat === 'a_valider' ? `Pièce n° ${String(piece.id)} : vérifiez ce qui a été lu.` : `Pièce n° ${String(piece.id)}, déjà ${ETATS_PIECE[piece.etat].toLowerCase()} : elle sera seulement jointe dans Zoho.`)),
      h(
        'div',
        { classe: 'champs-piece' },
        h('label', { classe: 'champ-piece' }, 'Fournisseur', fournisseur),
        h('label', { classe: 'champ-piece' }, 'Date de la facture', date),
        h('label', { classe: 'champ-piece' }, 'Montant TTC', montant),
        h('label', { classe: 'champ-piece' }, 'Devise', devise),
        aDeposer ? h('label', { classe: 'champ-piece' }, 'Rubrique du Drive', rubrique) : null,
      ),
      h('div', { classe: 'barre-actions' }, bouton),
    );

    bouton.addEventListener('click', () => {
      void (async () => {
        retour.effacer();
        const m = montantSaisi(montant.value);
        if (m === 'invalide') {
          retour.ko('Montant illisible : un nombre, avec une virgule pour les centimes.');
          return;
        }
        if (aDeposer && rubrique.value === '') {
          retour.ko('Choisissez la rubrique du Drive où classer la facture.');
          return;
        }
        // Rempli dans la fonction verrouillée : un objet, pour que le type ne se fige pas à `null`.
        const fait: { depot: DepotDrive | null } = { depot: null };
        try {
          await verrouille(controles, async () => {
            if (aDeposer) {
              const correction: CorrectionPiece = {
                champs: {
                  fournisseur: fournisseur.value.trim() || null,
                  date: date.value || null,
                  montant: m,
                  devise: devise.value.trim().toUpperCase() || null,
                },
                rubrique: rubrique.value,
              };
              await corrigerPiece(piece.id, correction);
              fait.depot = await deposerPiece(piece.id);
            }
            await joindreDansZoho(piece.id, ligne.id);
          });
          // La liste se relit : la ligne justifiée en sort, le message part en tête de page.
          const quoi = `${dateFr(ligne.date)} (${eurosCentimes(ligne.montant)})`;
          const depot = fait.depot;
          fini(
            depot === null
              ? `Pièce n° ${String(piece.id)} jointe dans Zoho à la dépense du ${quoi}.`
              : `« ${depot.nom} » ${depot.dejaPresent ? 'rattachée au fichier déjà dans le Drive' : 'déposée dans le Drive'} et jointe dans Zoho à la dépense du ${quoi}.`,
          );
        } catch (e) {
          const zoho = fait.depot !== null ? ' La pièce est déposée dans le Drive ; elle attend la jonction dans « Pièces reçues › À joindre dans Zoho ».' : '';
          retour.ko((e instanceof ErreurPiece ? messageDepotRefuse(e.motif, e.message) : messageDe(e, 'L’opération n’a pas abouti.')) + zoho);
        }
      })();
    });
  }

  return el;
}

/**
 * Zone de dépôt : accepte les fichiers glissés sur `cible` et un sélecteur ;
 * `recevoir` reçoit les fichiers choisis. `multiple` pour un lot.
 */
export function zoneDeDepot(cible: HTMLElement, recevoir: (fichiers: File[]) => void, multiple = false): HTMLInputElement {
  const entree = h('input', { type: 'file', accept: TYPES_FACTURE, hidden: true });
  entree.multiple = multiple;
  entree.addEventListener('change', () => {
    const f = Array.from(entree.files ?? []);
    entree.value = '';
    if (f.length > 0) recevoir(f);
  });
  let profondeur = 0;
  cible.addEventListener('dragenter', (e) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    profondeur += 1;
    cible.classList.add('depot-survol');
  });
  cible.addEventListener('dragover', (e) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  });
  cible.addEventListener('dragleave', () => {
    profondeur = Math.max(0, profondeur - 1);
    if (profondeur === 0) cible.classList.remove('depot-survol');
  });
  cible.addEventListener('drop', (e) => {
    e.preventDefault();
    profondeur = 0;
    cible.classList.remove('depot-survol');
    const f = Array.from(e.dataTransfer?.files ?? []);
    if (f.length > 0) recevoir(multiple ? f : f.slice(0, 1));
  });
  return entree;
}
